import { inject, NgModule, provideAppInitializer, isDevMode } from '@angular/core';
import { HttpClient, HttpHeaders, provideHttpClient } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule, SwPush } from '@angular/service-worker';
import { catchError, map, Observable, of } from 'rxjs';
import { provideTranslateService, TranslateCompiler, TranslateLoader, TranslateModule, TranslationObject } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';

// Default locales
import { IntlModule } from '@progress/kendo-angular-intl';
import "@progress/kendo-angular-intl/locales/it/all";
import "@progress/kendo-angular-intl/locales/en/all";
import "@progress/kendo-angular-intl/locales/fr/all";
import "@progress/kendo-angular-intl/locales/de/all";
import "@progress/kendo-angular-intl/locales/nl/all";
import "@progress/kendo-angular-intl/locales/es/all";
import "@progress/kendo-angular-intl/locales/pt/all";

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { UserService } from './services/user.service';
import { AuthGuard } from './auth/auth.guard';
import { environment } from 'src/environments/environment';
import { TripService } from './services/trip.service';
import { StorageService } from './services/storage.service';
import { ThemeService } from './services/theme.service';
import { LangCode } from './types/languages.type';

export class CustomTranslateLoader implements TranslateLoader {
	public contentHeader = new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*' });

	public constructor(private http: HttpClient, private storage: StorageService) {}

	public getTranslation(langCode: LangCode): Observable<TranslationObject> {
		const getFromStorage = (langCode: LangCode): Observable<TranslationObject> => {
			return new Observable((observer) => {
				this.storage.init().then(() => {
					this.storage.getTranslation(langCode).then((translation) => {
						observer.next(translation);
						observer.complete();
					});
				});
			});
		}

		if (!this.storage.isNetworkConnected$())
			return getFromStorage(langCode);

		const apiAddress = `${environment.servicesEndpointRoot}${environment.i18nFiles}${langCode}`;
		return this.http.get(apiAddress, { headers: this.contentHeader, withCredentials: true })
			.pipe(
				map((response: TranslationObject) => {
					this.storage.init().then(() => this.storage.setTranslation(langCode, response));
					return response;
				}),
				catchError((error) => {
					return getFromStorage(langCode);
				})
			);
	}
}

const TRANSLATE_SERVICE_CONFIG = {
	loader: {
		provide: TranslateLoader,
		useClass: CustomTranslateLoader,
		deps: [HttpClient, StorageService],
	},
	compiler: {
		provide: TranslateCompiler,
		useClass: TranslateMessageFormatCompiler,
	},
	useDefaultLang: true,
}

@NgModule({
	declarations: [
		AppComponent,
	],
	imports: [
		BrowserModule,
		IonicModule.forRoot(),
		IonicStorageModule.forRoot(),
		AppRoutingModule,
		IntlModule,
		TranslateModule.forRoot(TRANSLATE_SERVICE_CONFIG),
		ServiceWorkerModule.register('naar-service-worker.js', {
			registrationStrategy: 'registerWhenStable:30000',
		}),
	],
	providers: [
		AuthGuard,
		provideAppInitializer(async () => {
			const storage = inject(StorageService);
			const theme = inject(ThemeService);
				const user = inject(UserService);
			await storage.init();
			await theme.init();
				await user.init();
		}),
		provideHttpClient(),
		{
			provide: RouteReuseStrategy,
			useClass: IonicRouteStrategy,
		},
		provideTranslateService(TRANSLATE_SERVICE_CONFIG),

		//* Add here your stateful services, that need to be shared across the app
		//* Stateless services can be provided directly in the component that uses them
		TripService,
		{
			provide: SwPush,
			useClass: SwPush,
		}
	],
	bootstrap: [AppComponent],
})
export class AppModule {
}
